/**
 * OnboardingGuard component - Redirects to onboarding if not completed
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOnboardingStore, useAuthStore } from '@voxapilot/shared/stores';
import { ROUTES } from '../../config/constants';
import { ONBOARDING_STEPS, type OnboardingData } from '@voxapilot/shared/types';

interface OnboardingGuardProps {
  children: React.ReactNode;
}

// Routes that should bypass onboarding check
const ONBOARDING_ROUTES = [
  ROUTES.ONBOARDING,
  ROUTES.ONBOARDING_WELCOME,
  ROUTES.ONBOARDING_PROFILE,
  ROUTES.ONBOARDING_FIRST_MEETING,
  ROUTES.ONBOARDING_FEATURES,
  ROUTES.ONBOARDING_COMPLETE,
];

// Public routes that don't need auth or onboarding checks
const PUBLIC_ROUTES = [
  ROUTES.HOME,
  ROUTES.PRICING,
  ROUTES.ENTERPRISE,
  ROUTES.BLOG,
  ROUTES.INTEGRATIONS,
  ROUTES.SECURITY,
  ROUTES.ABOUT,
  ROUTES.CONTACT,
  ROUTES.CAREERS,
  ROUTES.HELP_CENTER,
  ROUTES.API,
  ROUTES.PRIVACY,
  ROUTES.TERMS,
  ROUTES.RESOURCES,
  ROUTES.SIGN_IN,
  ROUTES.SIGN_UP,
  ROUTES.FORGOT_PASSWORD,
  '/docs',
];

export function OnboardingGuard({ children }: OnboardingGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCompleted, skipped, currentStep, initialize } = useOnboardingStore();
  const { profile, isAuthenticated, isLoading } = useAuthStore();
  const [isChecking, setIsChecking] = useState(true);

  // Check if current route is a public route
  const isPublicRoute = PUBLIC_ROUTES.some((route) =>
    location.pathname === route || location.pathname.startsWith(route)
  );

  useEffect(() => {
    // For public routes, don't wait for auth - just render immediately
    if (isPublicRoute) {
      setIsChecking(false);
      return;
    }

    // Wait for auth to load
    if (isLoading) {
      return;
    }

    // Only check onboarding for authenticated users
    if (!isAuthenticated) {
      setIsChecking(false);
      return;
    }

    // If we're already on an onboarding route, don't redirect
    if (ONBOARDING_ROUTES.some((route) => location.pathname.startsWith(route))) {
      setIsChecking(false);
      return;
    }

    // Initialize onboarding state from profile
    if (profile) {
      initialize({
        onboardingCompleted: profile.onboardingCompleted,
        onboardingStep: profile.onboardingStep,
        onboardingSkipped: profile.onboardingSkipped,
        onboardingData: profile.onboardingData ? {
          profileSetup: profile.onboardingData.profileSetup ?? false,
          firstMeetingCreated: profile.onboardingData.firstMeetingCreated ?? false,
          featuresViewed: profile.onboardingData.featuresViewed ?? [],
        } as OnboardingData : undefined,
      });

      // If onboarding is not completed and not skipped, redirect to onboarding
      if (!profile.onboardingCompleted && !profile.onboardingSkipped) {
        // Redirect to appropriate onboarding step
        const step = profile.onboardingStep || currentStep || ONBOARDING_STEPS.WELCOME;

        switch (step) {
          case ONBOARDING_STEPS.WELCOME:
            navigate(ROUTES.ONBOARDING_WELCOME, { replace: true });
            break;
          case ONBOARDING_STEPS.PROFILE_SETUP:
            navigate(ROUTES.ONBOARDING_PROFILE, { replace: true });
            break;
          case ONBOARDING_STEPS.FIRST_MEETING:
            navigate(ROUTES.ONBOARDING_FIRST_MEETING, { replace: true });
            break;
          case ONBOARDING_STEPS.FEATURES_OVERVIEW:
            navigate(ROUTES.ONBOARDING_FEATURES, { replace: true });
            break;
          case ONBOARDING_STEPS.COMPLETION:
            navigate(ROUTES.ONBOARDING_COMPLETE, { replace: true });
            break;
          default:
            navigate(ROUTES.ONBOARDING_WELCOME, { replace: true });
        }
        return;
      }
    }

    setIsChecking(false);
  }, [isLoading, isAuthenticated, profile, isCompleted, skipped, currentStep, initialize, navigate, location.pathname, isPublicRoute]);

  // For public routes, don't show loading - render immediately
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // Show loading while checking (only for protected routes)
  if (isLoading || isChecking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Don't render children if redirecting
  if (!isLoading && isAuthenticated && profile && !profile.onboardingCompleted && !profile.onboardingSkipped && !ONBOARDING_ROUTES.some((route) => location.pathname.startsWith(route))) {
    return null;
  }

  return <>{children}</>;
}

